import React, { useState, useEffect } from 'react';
import { Plus, DollarSign, Trash2, Printer, Edit2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { openPrintWindow, getCompanyHeader, getDocumentInfo, getFooter } from '../utils/PrintUtils';

// Helper function to extract error message
const getErrorMessage = (error) => {
  if (!error.response?.data?.detail) {
    return 'Bir hata oluştu';
  }
  
  const detail = error.response.data.detail;
  
  // If detail is a string, return it
  if (typeof detail === 'string') {
    return detail;
  }
  
  // If detail is an array (Pydantic validation errors)
  if (Array.isArray(detail)) {
    return detail.map(err => {
      if (typeof err === 'string') return err;
      if (err.msg) return `${err.loc?.join('.') || 'Field'}: ${err.msg}`;
      return 'Validation error';
    }).join(', ');
  }
  
  // If detail is an object
  if (typeof detail === 'object') {
    return detail.message || detail.msg || JSON.stringify(detail);
  }
  
  return 'Bir hata oluştu';
};

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    invoice_id: '',
    amount: '',
    method: 'bank_transfer',
    bank: '',
    payment_date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });
  const [editFormData, setEditFormData] = useState({
    amount: '',
    method: 'bank_transfer',
    bank: '',
    payment_date: '',
    reference: '',
    notes: ''
  });

  // Türkiye bankaları
  const TURKISH_BANKS = [
    'Ziraat Bankası',
    'İş Bankası',
    'Garanti BBVA',
    'Akbank',
    'Yapı Kredi',
    'Vakıfbank',
    'Halkbank',
    'QNB Finansbank',
    'Denizbank',
    'TEB',
    'ING',
    'Kuveyt Türk',
    'Albaraka Türk',
    'Türkiye Finans',
    'Vakıf Katılım',
    'Ziraat Katılım',
    'HSBC',
    'Citibank',
    'Alternatifbank',
    'Anadolubank',
    'Şekerbank',
    'Odeabank'
  ].sort();
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [paymentsRes, invoicesRes] = await Promise.all([
        api.get('/payments'),
        api.get('/invoices')
      ]);
      setPayments(paymentsRes.data);
      // Filter to only show unpaid/partial invoices
      const unpaidInvoices = invoicesRes.data.filter(
        inv => inv.status === 'pending' || inv.status === 'partial'
      );
      setInvoices(unpaidInvoices);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Ödeme Makbuzu Yazdırma Fonksiyonu - PrintUtils kullanarak
  const printReceipt = (payment) => {
    const methodLabels = {
      'bank_transfer': 'Banka Havalesi',
      'cash': 'Nakit',
      'check': 'Çek',
      'credit_card': 'Kredi Kartı'
    };
    
    const header = getCompanyHeader({
      company_name: 'ROTA Maritime Agency'
    }, 'ÖDEME MAKBUZU');
    
    const infoFields = [
      { label: 'Makbuz No', value: `RCP-${payment.id.substring(0, 8).toUpperCase()}` },
      { label: 'Müşteri', value: payment.client_name },
      { label: 'Fatura No', value: payment.invoice_number || 'Bağımsız Ödeme' },
      { label: 'Ödeme Tarihi', value: new Date(payment.payment_date).toLocaleDateString('tr-TR') },
      { label: 'Ödeme Yöntemi', value: methodLabels[payment.method] || payment.method },
      ...(payment.bank ? [{ label: 'Banka', value: payment.bank }] : []),
      ...(payment.reference ? [{ label: 'Referans No', value: payment.reference }] : [])
    ];
    const info = getDocumentInfo(infoFields);
    
    const amountSection = `
      <div style="background: #f0fdf4; border: 2px solid #86efac; border-radius: 12px; padding: 25px; text-align: center; margin: 30px 0;">
        <div style="color: #166534; font-size: 14px; margin-bottom: 10px;">Ödenen Tutar</div>
        <div style="color: #059669; font-size: 36px; font-weight: 700;">
          ${payment.currency || '$'}${payment.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </div>
        <div style="text-align: center; margin-top: 15px;">
          <span style="border: 2px solid #16a34a; color: #16a34a; padding: 5px 15px; display: inline-block; transform: rotate(-5deg); font-weight: bold;">ÖDENDİ</span>
        </div>
      </div>
    `;
    
    const notesSection = payment.notes ? `
      <div style="margin-top: 15px; padding: 10px; background: #f8fafc; border-radius: 6px;">
        <strong>Notlar:</strong> ${payment.notes}
      </div>
    ` : '';
    
    const footer = getFooter({ company_name: 'ROTA Maritime Agency' });
    
    const content = `
      <div style="max-width: 500px; margin: 0 auto; padding: 20px;">
        ${header}
        ${info}
        ${amountSection}
        ${notesSection}
        ${footer}
        <div style="text-align: center; margin-top: 10px; font-size: 11px; color: #666;">
          İşlemi Yapan: ${payment.recorded_by_name || '-'}
        </div>
      </div>
    `;
    
    openPrintWindow(content, `Ödeme Makbuzu - ${payment.client_name}`);
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setEditFormData({
      amount: payment.amount.toString(),
      method: payment.method,
      bank: payment.bank || '',
      payment_date: payment.payment_date ? payment.payment_date.split('T')[0] : '',
      reference: payment.reference || '',
      notes: payment.notes || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Handle both comma and dot as decimal separator
    const amountStr = String(editFormData.amount).replace(',', '.');
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Geçerli bir tutar giriniz');
      return;
    }
    
    try {
      await api.put(`/payments/${editingPayment.id}`, {
        amount: amount,
        method: editFormData.method,
        bank: editFormData.bank || null,
        payment_date: editFormData.payment_date,
        reference: editFormData.reference || null,
        notes: editFormData.notes || null
      });
      
      toast.success('Ödeme güncellendi');
      setShowEditModal(false);
      setEditingPayment(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleInvoiceSelect = (e) => {
    const invoiceId = e.target.value;
    setFormData({ ...formData, invoice_id: invoiceId });
    
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (invoice) {
      setSelectedInvoice(invoice);
      setFormData(prev => ({ ...prev, amount: invoice.balance.toFixed(2) }));
    } else {
      setSelectedInvoice(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Handle both comma and dot as decimal separator
    const amountStr = String(formData.amount).replace(',', '.');
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Tutar 0\'dan büyük olmalıdır');
      return;
    }
    
    // Fazla ödeme uyarısı (engelleme değil)
    if (selectedInvoice && amount > selectedInvoice.balance + 0.01) {
      if (!window.confirm(`Ödeme tutarı (${amount.toFixed(2)}) fatura bakiyesinden (${selectedInvoice.balance.toFixed(2)}) fazla. Devam etmek istiyor musunuz?`)) {
        return;
      }
    }
    
    try {
      // Prepare payload with proper date format
      const payload = {
        invoice_id: formData.invoice_id,
        amount: amount,
        method: formData.method,
        reference: formData.reference || null,
        notes: formData.notes || null
      };
      
      // Add payment_date as ISO string
      if (formData.payment_date) {
        payload.payment_date = new Date(formData.payment_date).toISOString();
      }
      
      // Add bank only if method is bank_transfer
      if (formData.method === 'bank_transfer' && formData.bank) {
        payload.bank = formData.bank;
      }
      
      await api.post('/payments', payload);
      toast.success('Ödeme başarıyla kaydedildi');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_id: '',
      amount: '',
      method: 'bank_transfer',
      bank: '',
      payment_date: new Date().toISOString().split('T')[0],
      reference: '',
      notes: ''
    });
    setSelectedInvoice(null);
  };

  const handleDelete = async (paymentId, paymentReference) => {
    if (!window.confirm(`Are you sure you want to delete payment "${paymentReference}"?`)) return;
    
    try {
      await api.delete(`/payments/${paymentId}`);
      toast.success('Ödeme başarıyla silindi');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Ödeme tablosu render fonksiyonu
  const renderPaymentTable = (paymentList, title, bgColor) => {
    if (paymentList.length === 0) return null;
    
    const totalAmount = paymentList.reduce((sum, p) => sum + p.amount, 0);
    
    return (
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0.75rem 1rem', 
          backgroundColor: bgColor, 
          color: 'white',
          borderRadius: '8px 8px 0 0',
          fontWeight: '600'
        }}>
          <span>{title}</span>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>
              Toplam: ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
            <span style={{ 
              backgroundColor: 'rgba(255,255,255,0.2)', 
              padding: '0.25rem 0.75rem', 
              borderRadius: '12px',
              fontSize: '0.875rem'
            }}>
              {paymentList.length}
            </span>
          </div>
        </div>
        <div className="table-container" style={{ borderRadius: '0 0 8px 8px', border: `1px solid ${bgColor}`, borderTop: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Fatura</th>
                <th>Müşteri</th>
                <th>Tutar</th>
                <th>Banka</th>
                <th>Referans</th>
                <th>Kaydeden</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {paymentList.map((payment) => (
                <tr key={payment.id} data-testid={`payment-row-${payment.id}`}>
                  <td>{new Date(payment.payment_date).toLocaleDateString('tr-TR')}</td>
                  <td><strong>{payment.invoice_number || 'Bağımsız'}</strong></td>
                  <td>{payment.client_name}</td>
                  <td><strong style={{ color: '#059669' }}>${payment.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></td>
                  <td>{payment.bank || '-'}</td>
                  <td>{payment.reference || '-'}</td>
                  <td>{payment.recorded_by_name}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="action-btn" onClick={() => printReceipt(payment)} title="Makbuz"><Printer size={18} /></button>
                      <button className="action-btn action-btn-edit" onClick={() => handleEdit(payment)} title="Düzenle"><Edit2 size={18} /></button>
                      <button className="action-btn action-btn-danger" onClick={() => handleDelete(payment.id, payment.reference || payment.invoice_number)} title="Sil"><Trash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title" data-testid="page-title">Ödemeler</h1>
        <button 
          className="btn btn-success" 
          onClick={() => setShowModal(true)}
          data-testid="record-payment-btn"
        >
          <Plus size={18} />
          Ödeme Kaydet
        </button>
      </div>
      
      <div style={{ padding: '2rem' }}>
        {loading ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>Yükleniyor...</div></div>
        ) : payments.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="empty-state">
                <div className="empty-state-icon"><DollarSign /></div>
                <h3 className="empty-state-title">Henüz ödeme kaydı yok</h3>
                <p className="empty-state-text">İlk ödemenizi kaydedin</p>
                <button className="btn btn-success" onClick={() => setShowModal(true)}>
                  <Plus size={18} />
                  Ödeme Kaydet
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Özet Kartları */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{payments.filter(p => p.method === 'bank_transfer').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Havale</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{payments.filter(p => p.method === 'cash').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Nakit</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{payments.filter(p => p.method === 'credit_card').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Kredi Kartı</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{payments.filter(p => !['bank_transfer', 'cash', 'credit_card'].includes(p.method)).length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Diğer</div>
              </div>
            </div>

            {/* Yönteme Göre Gruplandırılmış Tablolar */}
            {renderPaymentTable(payments.filter(p => p.method === 'bank_transfer'), '🏦 Banka Havalesi', '#3b82f6')}
            {renderPaymentTable(payments.filter(p => p.method === 'cash'), '💵 Nakit Ödemeler', '#10b981')}
            {renderPaymentTable(payments.filter(p => p.method === 'credit_card'), '💳 Kredi Kartı', '#8b5cf6')}
            {renderPaymentTable(payments.filter(p => !['bank_transfer', 'cash', 'credit_card'].includes(p.method)), '📋 Diğer Ödemeler', '#f59e0b')}
          </>
        )}
      </div>

      {/* Ödeme Kaydet Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="modal-title">Ödeme Kaydet</h2>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                <div className="form-group">
                  <label className="form-label">Invoice *</label>
                  <select
                    className="form-select"
                    value={formData.invoice_id}
                    onChange={handleInvoiceSelect}
                    required
                    data-testid="invoice-select"
                  >
                    <option value="">Fatura Seçin</option>
                    {invoices.map(invoice => (
                      <option key={invoice.id} value={invoice.id}>
                        {invoice.number} - {invoice.client_name} (Balance: ${invoice.balance.toFixed(2)})
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedInvoice && (
                  <div style={{ background: '#f1f5f9', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Amount</div>
                        <div style={{ fontWeight: 600 }}>${selectedInvoice.total.toFixed(2)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Paid</div>
                        <div style={{ fontWeight: 600, color: 'var(--success)' }}>${selectedInvoice.paid_amount.toFixed(2)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Balance Due</div>
                        <div style={{ fontWeight: 600, color: 'var(--danger)' }}>${selectedInvoice.balance.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Ödeme Tutarı *</label>
                    <input
                      type="text"
                      className="form-input"
                      inputMode="decimal"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0,00 veya 0.00"
                      required
                      data-testid="amount-input"
                    />
                    <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                      Virgül veya nokta kullanabilirsiniz (örn: 4540,81 veya 4540.81)
                    </small>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Ödeme Tarihi *</label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.payment_date}
                      onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                      required
                      data-testid="payment-date-input"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Ödeme Yöntemi *</label>
                    <select
                      className="form-select"
                      value={formData.method}
                      onChange={(e) => setFormData({ ...formData, method: e.target.value, bank: '' })}
                      required
                      data-testid="method-select"
                    >
                      <option value="bank_transfer">Banka Havalesi</option>
                      <option value="cash">Nakit</option>
                      <option value="credit_card">Kredi Kartı</option>
                      <option value="check">Çek</option>
                    </select>
                  </div>
                  
                  {formData.method === 'bank_transfer' && (
                    <div className="form-group">
                      <label className="form-label">Banka *</label>
                      <select
                        className="form-select"
                        value={formData.bank}
                        onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                        required
                        data-testid="bank-select"
                      >
                        <option value="">Banka Seçin</option>
                        {TURKISH_BANKS.map(bank => (
                          <option key={bank} value={bank}>{bank}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">Reference Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    placeholder="Transaction ID, Çek number, etc."
                    data-testid="reference-input"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Notlar</label>
                  <textarea
                    className="form-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    data-testid="notes-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  İptal
                </button>
                <button type="submit" className="btn btn-success" data-testid="save-payment-btn">
                  Ödeme Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Payment Modal */}
      {showEditModal && editingPayment && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title">Ödeme Düzenle</h2>
              <button className="modal-close" onClick={() => { setShowEditModal(false); setEditingPayment(null); }}>×</button>
            </div>
            <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                {/* Fatura ve Müşteri Bilgileri (salt okunur) */}
                <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Fatura No:</span>
                      <strong style={{ marginLeft: '0.5rem' }}>{editingPayment.invoice_number || 'Bağımsız Ödeme'}</strong>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Müşteri:</span>
                      <strong style={{ marginLeft: '0.5rem' }}>{editingPayment.client_name}</strong>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Tutar</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-input"
                    value={editFormData.amount}
                    onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                    placeholder="0,00 veya 0.00"
                    required
                  />
                  <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                    Virgül veya nokta kullanabilirsiniz
                  </small>
                </div>

                <div className="form-group">
                  <label className="form-label">Ödeme Yöntemi</label>
                  <select
                    className="form-input"
                    value={editFormData.method}
                    onChange={(e) => setEditFormData({ ...editFormData, method: e.target.value })}
                  >
                    <option value="bank_transfer">Banka Havalesi</option>
                    <option value="cash">Nakit</option>
                    <option value="check">Çek</option>
                    <option value="credit_card">Kredi Kartı</option>
                  </select>
                </div>

                {editFormData.method === 'bank_transfer' && (
                  <div className="form-group">
                    <label className="form-label">Banka</label>
                    <select
                      className="form-input"
                      value={editFormData.bank}
                      onChange={(e) => setEditFormData({ ...editFormData, bank: e.target.value })}
                    >
                      <option value="">Banka Seçin</option>
                      {TURKISH_BANKS.map(bank => (
                        <option key={bank} value={bank}>{bank}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Ödeme Tarihi</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editFormData.payment_date}
                    onChange={(e) => setEditFormData({ ...editFormData, payment_date: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Referans No</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editFormData.reference}
                    onChange={(e) => setEditFormData({ ...editFormData, reference: e.target.value })}
                    placeholder="Dekont/Referans numarası"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Notlar</label>
                  <textarea
                    className="form-input"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    rows="2"
                    placeholder="Ek notlar..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowEditModal(false); setEditingPayment(null); }}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;